import { useMemo } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

/**
 * Two-dropdown month/year picker.
 *
 * Stores and emits the same `YYYY-MM` string format the rest of the app
 * already uses, so existing reads/writes don't need to change. Designed
 * for life-history fields (placements, attendance, care periods) where:
 *
 *  - exact day is rarely known
 *  - users often pick decades-old dates
 *  - native `<input type="month">` is a very inconsistent UX across
 *    desktop and mobile, especially for older years
 *
 * Year range defaults to (currentYear - 60) → currentYear, which covers
 * any reasonable adult care leaver using the site (a 60-year-old today
 * was born in 1966 and started school in the 70s).
 */
const MONTHS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

export const MonthYearPicker = ({
  value = '',
  onChange,
  yearsBack = 60,
  disabled = false,
  className = '',
  testId,
  ariaLabel,
}) => {
  const years = useMemo(() => {
    const current = new Date().getFullYear();
    const list = [];
    for (let y = current; y >= current - yearsBack; y -= 1) list.push(String(y));
    return list;
  }, [yearsBack]);

  const [year = '', month = ''] = (value || '').split('-');

  const emit = (nextYear, nextMonth) => {
    if (!nextYear && !nextMonth) {
      onChange?.('');
      return;
    }
    if (nextYear && nextMonth) {
      onChange?.(`${nextYear}-${nextMonth}`);
    } else {
      // Hold the partial selection in state so the user sees it, but only
      // emit a real value once both halves are picked. We achieve this by
      // emitting an empty string until then.
      onChange?.('');
    }
  };

  return (
    <div className={`grid grid-cols-2 gap-2 ${className}`} data-testid={testId}>
      <Select
        value={month}
        onValueChange={(m) => emit(year, m)}
        disabled={disabled}
      >
        <SelectTrigger
          aria-label={ariaLabel ? `${ariaLabel} month` : 'Month'}
          data-testid={testId ? `${testId}-month` : undefined}
        >
          <SelectValue placeholder="Month" />
        </SelectTrigger>
        <SelectContent>
          {MONTHS.map((m) => (
            <SelectItem key={m.value} value={m.value}>
              {m.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={year}
        onValueChange={(y) => emit(y, month)}
        disabled={disabled}
      >
        <SelectTrigger
          aria-label={ariaLabel ? `${ariaLabel} year` : 'Year'}
          data-testid={testId ? `${testId}-year` : undefined}
        >
          <SelectValue placeholder="Year" />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {years.map((y) => (
            <SelectItem key={y} value={y}>
              {y}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default MonthYearPicker;
